import {
  dedupeOrganizationTypeValues,
  fetchAllOrganizationTypes,
  mapOrganizationTypeValues,
} from './organization-type-options.shared';
import { OnOptionsLoad } from './types';

/** Synthetic autocomplete value; stripped before API requests. */
export const ALL_SCHOOLS_ORGANIZATION_TYPE_ID = -1;

export const ALL_SCHOOLS_ORGANIZATION_TYPE_OPTION: {
  id: number;
  name: string;
} = {
  id: ALL_SCHOOLS_ORGANIZATION_TYPE_ID,
  name: 'All Schools',
};

export const isAllSchoolsOrganizationTypeOption = (
  item: { id?: number | string } | null | undefined,
): boolean => Number(item?.id) === ALL_SCHOOLS_ORGANIZATION_TYPE_ID;

/** Post-secondary org types excluded from "All Schools" (K–12 and below only). */
export const EXCLUDED_ALL_SCHOOLS_ORGANIZATION_TYPE_NAMES = [
  'Higher education',
] as const;

const normalizedExcludedAllSchoolsNames = new Set(
  EXCLUDED_ALL_SCHOOLS_ORGANIZATION_TYPE_NAMES.map((name) =>
    name.trim().toLowerCase(),
  ),
);

export const isExcludedFromAllSchoolsOrganizationTypeName = (
  name: string | undefined,
): boolean => {
  if (!name) {
    return false;
  }

  return normalizedExcludedAllSchoolsNames.has(name.trim().toLowerCase());
};

export const isSchoolOrEducationOrganizationTypeName = (
  name: string | undefined,
): boolean => {
  if (!name || isExcludedFromAllSchoolsOrganizationTypeName(name)) {
    return false;
  }

  const lowerName = name.toLowerCase();
  return (
    lowerName.includes('education') ||
    lowerName.includes('school') ||
    lowerName.includes('educational')
  );
};

export async function loadSchoolOrganizationTypes(
  onOptionsLoad: OnOptionsLoad,
): Promise<Array<{ id: number; name: string }>> {
  const allOrgTypes = await fetchAllOrganizationTypes(onOptionsLoad);

  return mapOrganizationTypeValues(
    allOrgTypes.filter((orgType) =>
      isSchoolOrEducationOrganizationTypeName(orgType.name),
    ),
  );
}

export async function resolveAllSchoolsOrganizationTypes(
  onOptionsLoad: OnOptionsLoad,
  currentValue: Array<{ id: number; name: string }> | undefined,
): Promise<Array<{ id: number; name: string }>> {
  const schoolTypes = await loadSchoolOrganizationTypes(onOptionsLoad);

  const withoutAllSchools = (currentValue ?? [])
    .filter((item) => !isAllSchoolsOrganizationTypeOption(item))
    .map((item) => ({
      id: typeof item.id === 'number' ? item.id : Number(item.id),
      name: item.name ?? '',
    }));

  return dedupeOrganizationTypeValues([...withoutAllSchools, ...schoolTypes]);
}
